import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { IconBell, IconCalendar, IconEdit, IconEye, IconSearch, IconSettings } from "@/lib/icons";

export interface CrumbItem {
  /** Texto visível. */
  label: string;
  /** Quando informado, o item vira link. */
  to?: string;
}

export interface PageHeaderProps {
  crumbs: CrumbItem[];
  title: string;
  /** Badge à direita do título (ex.: status do workflow). */
  titleBadge?: ReactNode;
  /** Itens da faixa de metadados abaixo do título. */
  meta?: ReactNode;
  /** Modo atual (`view` ou `edit`). Quando ausente, esconde o toggle. */
  mode?: "view" | "edit";
  onModeChange?: (mode: "view" | "edit") => void;
  settingsTo?: string;
  /** Com rotulo, a configuracao vira botao legivel em vez de engrenagem sem texto. */
  settingsLabel?: string;
  actions?: ReactNode;
  /** Controles/contexto do periodo, sem uma segunda caixa introdutoria. */
  context?: ReactNode;
}

/**
 * Cabeçalho fixo da `.main`. Mostra breadcrumb + título + meta + ações.
 *
 * Os botões "Auto" (pausar refresh) e "Recarregar" foram removidos: com a
 * reconciliação por valor no `useAsync`, refresh em background é silencioso
 * (não causa re-render quando o JSON é igual), e quando o operador precisa
 * forçar atualização total ele usa F5/Ctrl+R. Pausa explícita também não
 * faz mais sentido — quando há modal aberto, o refresh já é pausado
 * automaticamente em `MachineDashboardPage`.
 *
 * Os botões de busca/notificação seguem como placeholders desabilitados —
 * funcionalidade real entra nas próximas fases.
 */
export function PageHeader({
  crumbs,
  title,
  titleBadge,
  meta,
  mode,
  onModeChange,
  settingsTo,
  settingsLabel,
  actions,
  context,
}: PageHeaderProps) {
  return (
    <div className={context ? "ph ph--context" : "ph"}>
      <div className="ph-l">
        <div className="crumb">
          {crumbs.map((item, index) => (
            <span
              key={`${item.label}-${index}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {item.to ? (
                <Link to={item.to}>{item.label}</Link>
              ) : (
                <b>{item.label}</b>
              )}
              {index < crumbs.length - 1 ? <span className="sep">/</span> : null}
            </span>
          ))}
        </div>
        <div className="ph-title">
          <h1>{title}</h1>
          {titleBadge}
        </div>
        {meta ? <div className="ph-meta">{meta}</div> : null}
      </div>

      <div className="ph-r">
        {actions}
        {mode && onModeChange ? (
          <div className="seg" role="group" aria-label="Modo de operação">
            <button
              type="button"
              className={mode === "view" ? "on" : ""}
              onClick={() => onModeChange("view")}
              aria-pressed={mode === "view"}
            >
              <IconEye size={12} /> Visualização
            </button>
            <button
              type="button"
              className={mode === "edit" ? "on" : ""}
              onClick={() => onModeChange("edit")}
              aria-pressed={mode === "edit"}
            >
              <IconEdit size={12} /> Edição
            </button>
          </div>
        ) : null}
        {settingsTo && settingsLabel ? <Link className="btn sm ph-settings" to={settingsTo} title="Configurações do workflow"><IconCalendar size={14} /> <span className="ph-settings-label">{settingsLabel}</span></Link>
          : settingsTo ? <Link className="iconbtn" to={settingsTo} aria-label="Configurações do workflow" title="Configurações do workflow"><IconSettings size={16} /></Link> : <button
          type="button"
          className="iconbtn"
          aria-label="Buscar (em breve)"
          title="Busca em breve"
          disabled
        >
          <IconSearch size={14} />
        </button>}
        <button
          type="button"
          className="iconbtn"
          aria-label="Notificações (em breve)"
          title="Notificações em breve"
          disabled
        >
          <IconBell size={14} />
        </button>
      </div>
      {context ? <div className="ph-context">{context}</div> : null}
    </div>
  );
}
